import { Box, Skeleton, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { Tenant } from '@sw/tenant-types';
import { tokens } from '../theme/tokens';
import { useTenant } from '../lib/tenant';

export function TenantBrand({ size = 'card' }: { size?: 'card' | 'sidebar' }) {
  const { tenant, isLoading } = useTenant();
  return <BrandBlock size={size} isLoading={isLoading} tenant={tenant} />;
}

function BrandBlock({
  size,
  isLoading,
  tenant,
}: {
  size: 'card' | 'sidebar';
  isLoading: boolean;
  tenant: Tenant | null;
}) {
  const { t } = useTranslation();
  const dims = size === 'card'
    ? { box: 44, font: 17, radius: 12, nameSize: 16, subSize: 10.5 }
    : { box: 36, font: 14, radius: 10, nameSize: 14.5, subSize: 9.5 };

  if (isLoading) {
    return (
      <Box
        data-testid="tenant-brand-loading"
        sx={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0 }}
      >
        <Skeleton variant="rounded" width={dims.box} height={dims.box} />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: 0 }}>
          <Skeleton variant="text" width={100} sx={{ fontSize: dims.nameSize }} />
          <Skeleton variant="text" width={70} sx={{ fontSize: dims.subSize }} />
        </Box>
      </Box>
    );
  }

  const name = tenant?.name ?? t('tenant.fallbackName');
  const subtitle = t('app.name');
  const initials = computeInitials(name);
  const background = tenant?.primaryColor
    ? gradientFromHex(tenant.primaryColor)
    : tokens.brand.fallback;
  // Defense in depth: only render logo if backend returned an https URL. Backend should
  // validate this on write; this guard prevents javascript:/data:/http: from reaching the
  // DOM even if a misconfigured workspace slips through.
  const safeLogo = isHttpsUrl(tenant?.logoUrl) ? tenant!.logoUrl! : null;

  return (
    <Box
      data-testid="tenant-brand"
      sx={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0 }}
    >
      <Box
        sx={{
          width: dims.box,
          height: dims.box,
          borderRadius: `${dims.radius}px`,
          display: 'grid',
          placeItems: 'center',
          color: '#fff',
          fontWeight: 700,
          fontSize: dims.font,
          letterSpacing: '-0.02em',
          flexShrink: 0,
          boxShadow: 'inset 0 0 0 1px rgba(255,255,255,.08)',
          background,
          overflow: 'hidden',
        }}
      >
        {safeLogo ? (
          <Box
            component="img"
            src={safeLogo}
            alt=""
            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          initials
        )}
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 }}>
        <Typography
          data-testid="tenant-name"
          sx={{
            fontSize: dims.nameSize,
            fontWeight: 700,
            letterSpacing: '-0.015em',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {name}
        </Typography>
        <Typography
          sx={{
            fontFamily: tokens.font.mono,
            fontSize: dims.subSize,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            color: tokens.color.muted,
          }}
        >
          {subtitle}
        </Typography>
      </Box>
    </Box>
  );
}

function computeInitials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '·';
  if (words.length === 1) return words[0]!.slice(0, 2).toUpperCase();
  return (words[0]![0]! + words[1]![0]!).toUpperCase();
}

function gradientFromHex(hex: string): string {
  const cleaned = hex.trim().toLowerCase();
  // Strict hex match prevents any CSS escape vector — only #RRGGBB lowercase passes.
  if (!/^#[0-9a-f]{6}$/.test(cleaned)) return tokens.brand.fallback;
  return `linear-gradient(140deg, ${cleaned}, ${darken(cleaned, 0.35)})`;
}

function darken(hex: string, amount: number): string {
  const num = parseInt(hex.slice(1), 16);
  const r = Math.max(0, Math.round((num >> 16) * (1 - amount)));
  const g = Math.max(0, Math.round(((num >> 8) & 0xff) * (1 - amount)));
  const b = Math.max(0, Math.round((num & 0xff) * (1 - amount)));
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
}

function isHttpsUrl(url: string | null | undefined): boolean {
  if (!url) return false;
  try {
    return new URL(url).protocol === 'https:';
  } catch {
    return false;
  }
}

